import React, { useState, useMemo, useEffect } from "react";
import { Link, Route, Routes, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Auth, useAuth } from "../auth/AuthContext";
import { OperationError, issueTextOrDefault } from "../DAO";
import { DocumentTitle } from "../DocumentTitle";
import { formatIsoDate } from "../Format";
import { InstanceStore, useInstanceStore } from "../instance/InstanceContext";
import { Licence } from "./Licence";
import { listLicences } from "./LicenceDAO";
import { LicenceForm } from "./LicenceForm";
import { LicenceList } from "./LicenceList";


export const LicencePage = () => {
   const auth: Auth = useAuth();
   if (!auth?.user?.permissions?.licence?.canView) {
      return <div>You do not have permission to be here."</div>;
   }

   const [allLicences, setAllLicences] = useState<Licence[] | null>(null);
   const [loadLicErrMsgs, setLoadLicErrMsgs] = useState<string[] | null>(null);

   const errMsgs: string[] | null = useMemo(() => {
      return loadLicErrMsgs;
   }, [loadLicErrMsgs]);
   const isLoading = useMemo<boolean>(() => {
      return allLicences == null && errMsgs == null;
   }, [allLicences, errMsgs]);

   async function loadLicences(): Promise<void> {
      setAllLicences(null);
      setLoadLicErrMsgs(null);
      return listLicences()
         .catch((err: OperationError) => {
            setLoadLicErrMsgs(err.issues.map(issueTextOrDefault));
            return null;
         })
         .then(setAllLicences);
   }

   useEffect(() => {
      // initialize
      loadLicences();
   }, []);

   const instanceStore: InstanceStore = useInstanceStore();
   const [instanceId, setInstanceId] = useState<number | null>(null);
   const [activeOnly, setActiveOnly] = useState<boolean>(true);

   const [searchParams, setSearchParams] = useSearchParams();
   useEffect(() => {
      const instanceParam: string | null = searchParams.get("instance");
      if (instanceParam && isIntegerPattern(instanceParam) && instanceStore.instances?.length) {
         const requestedInstanceId: number = +instanceParam;
         if (instanceStore.instances.some(i => i.id === requestedInstanceId)) {
            setInstanceId(requestedInstanceId);
            setSearchParams({}, { replace: true }); // Clear it from the URL after using it
         }
      }
   }, [searchParams, instanceStore.instances]);

   const filteredLicences = useMemo<Licence[] | null>(() => {
      if (allLicences == null) {
         return null;
      }
      else if (instanceId != null && activeOnly) {
         return allLicences.filter(lic => lic.instanceId === instanceId && lic.active);
      }
      else if (activeOnly) {
         return allLicences.filter(lic => lic.active);
      }
      else if (instanceId != null) {
         return allLicences.filter(lic => lic.instanceId === instanceId);
      }
      return allLicences;
   }, [allLicences, instanceId, activeOnly]);

   const canEdit = useMemo<boolean>(() => {
      return auth?.user?.permissions?.licence?.canEdit;
   }, [auth]);

   const htmlId = (suffix?: string): string => {
      const prefix = "licence-page";
      return suffix ? prefix + "_" + suffix : prefix;
   }
   return (
      <DocumentTitle title="Licences">
         <article id={htmlId()}>
            {isLoading && <div id={htmlId("loading")}>Loading...</div>
               || errMsgs && (
                  <div id={htmlId("load-errors")}>
                     {errMsgs.map(msg => <span className="inline-error-message">{msg}</span>).join("\n")}
                     <button type="button" className="link" onClick={loadLicences}>try again</button>
                  </div>
               )
               || filteredLicences && (
                  <>
                     <div>
                        {instanceStore.instances?.length &&
                           <>
                              <label htmlFor={htmlId("instance")}>Filter to instance:</label>
                              <select id={htmlId("instance")} value={instanceId ?? ""}
                                 onChange={e => {
                                    const val = e.target.value;
                                    setInstanceId(val === "" ? null : +val);
                                 }} style={{ marginRight: "15px" }}>
                                 <option value="">-- All --</option>
                                 {instanceStore.instances?.map(i =>
                                    <option key={i.id} value={i.id}>{i.name}</option>)}
                              </select>
                           </>
                        }
                        <label htmlFor={htmlId("active-only")}>Active only:</label>
                        <input id={htmlId("active-only")}
                           type="checkbox"
                           checked={activeOnly}
                           onChange={() => setActiveOnly(prevValue => !prevValue)} />
                     </div>
                     <Routes>
                        <Route path="" element={<>
                           <LicenceList licences={filteredLicences} />
                           {canEdit && <Link to="new" id={htmlId("new")} className="link">new</Link>}
                        </>} />

                        <Route path=":id" element={<>
                           <LicenceListWithSelection licences={filteredLicences} />
                        </>} />

                        <Route path=":id/:action" element={<>
                           <LicenceListWithSelection licences={filteredLicences} reloadFunc={loadLicences} />
                        </>} />

                        <Route path="new" element={<>
                           <LicenceList licences={filteredLicences} />
                           {canEdit && <>
                              <DocumentTitle title="New" />
                              <LicenceForm licence={null} action="add" onSave={loadLicences} />
                           </>}
                        </>} />
                     </Routes>
                  </>)
            }
         </article>
      </DocumentTitle>
   );
};

interface Props {
   licences: Licence[];
   reloadFunc?: () => void;
}
const LicenceListWithSelection = ({ licences, reloadFunc }: Props): JSX.Element => {
   const { id: idParam, action: actionParam } = useParams();

   let lic: Licence | null = null;
   if (licences != null && idParam != null && isIntegerPattern(idParam)) { // id param is number format
      const asNum = +idParam;
      lic = licences.find(lic => lic.id === asNum) ?? null;
   }

   if (lic == null) {
      return <LicenceList licences={licences} />
   }

   if (actionParam === "edit") {
      const navigate = useNavigate();
      navigate(lic.lastSync != null ? "replace" : "update", { replace: true })
   }

   let title = `id: ${lic.id}`;
   let inlineForm = true;
   let action: 'replace' | 'update' | 'view' | 'add' = 'view';
   switch (actionParam) {
      case "clone":
         // Make a copy of original licence, nullifying some properties
         lic = Object.assign({}, lic, { id: null, endDate: null, startDate: formatIsoDate(new Date) });
         title = "Clone - " + title;
         inlineForm = false;
         action = 'add';
         break;
      case "replace":
         title = "Replace - " + title;
         action = 'replace';
         break;
      case "update":
         title = "Update - " + title;
         action = 'update';
         break;
      case "view":
         break;
      default:
         break;
   }

   const form: JSX.Element | null = lic ?
      <DocumentTitle title={title}>
         <LicenceForm licence={lic} action={action} onSave={reloadFunc} />
      </DocumentTitle> : null;

   return <>
      <LicenceList licences={licences} selectedId={lic?.id} form={form} />
      {inlineForm ? <></> : form}
   </>;
};

function isIntegerPattern(string: string): boolean {
   return /^\d+$/.test(string);
}
